import React from 'react';
import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import {
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  BarChart,
  Cell,
  Legend,
} from 'recharts';
import { lighten } from "@material-ui/core/styles";
import Skeleton from '@material-ui/lab/Skeleton';
import Grid from '@material-ui/core/Grid';
import Slider from '@material-ui/core/Slider';
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import MenuItem from "@material-ui/core/MenuItem";
import Paper from "@material-ui/core/Paper";
import { withStyles } from "@material-ui/core/styles";

import BaseWeekStoreDashboardView from '../BaseWeekStoreDashboardView';
import { floatFormatter, formatFloat, unitFormatter } from '../../utils';
import { athenaBlue, athenaGreen, statRed } from '../../globals';
import FilterListView from "../../components/FilterListView";
import Header from "../../components/utils/Header";
import DownloadButton from "../../components/utils/DownloadButton";
import DataFrame from "../../components/utils/DataFrame";


const styles = theme => ({
  belowMin: {
    color: statRed,
  },
  closeToTarget: {
    color: athenaGreen,
  },
  aboveMax: {
    color: athenaBlue,
  },
});


@withStyles(styles)
@observer
class InventoryView extends BaseWeekStoreDashboardView {
  static apiSubPath = '/inventory/';

  static units = {
    TO: 'Tons',
    KPC: 'Pieces',
    L: 'Liters',
    kEUR: 'Euros',
    SKUs: 'SKUs',
  };
  static tableIndexNames = {
    'MAT_COD': 'SKU code',
    'MAT_DSC': 'SKU name',
  };
  static tableColumns = {
    'total_stock_TO': 'Current stock (t)',
    'stock_min_TO': 'Min stock (t)',
    'stock_target_TO': 'Target stock (t)',
    'stock_max_TO': 'Max stock (t)',
    'stock_gap_TO': 'Gap vs target stock (t)',

    'total_stock_L': 'Current stock (L)',
    'stock_min_L': 'Min stock (L)',
    'stock_target_L': 'Target stock (L)',
    'stock_max_L': 'Max stock (L)',
    'stock_gap_L': 'Gap vs target stock (L)',

    'total_stock_kEUR': 'Current stock (k€)',
    'stock_min_kEUR': 'Min stock (k€)',
    'stock_target_kEUR': 'Target stock (k€)',
    'stock_max_kEUR': 'Max stock (k€)',
    'stock_gap_kEUR': 'Gap vs target stock (k€)',

    'total_stock_DOH': 'Current stock (DOH)',
    'stock_min_DOH': 'Min stock (DOH)',
    'stock_target_DOH': 'Target stock (DOH)',
    'stock_max_DOH': 'Max stock (DOH)',
    'stock_gap_DOH': 'Gap vs target stock (DOH)',
  };

  @observable graphWeekIndex = 0;
  @observable projectedWeeks = [];
  graphData = [];
  @observable tableData = [];
  @observable unit = Object.keys(this.constructor.units)[0];
  @observable activeLabel = null;

  _setData(data) {
    if ('week_store' in data && data.week_store) {
      this.weekStoreId = parseInt(data.week_store);
    }
    if (('graph_week_index' in data) && (data.graph_week_index !== null)) {
      this.graphWeekIndex = parseInt(data.graph_week_index);
    }
    if ('projected_weeks' in data) {
      this.projectedWeeks = data.projected_weeks;
    }
    if ('graph_data' in data) {
      this.graphData = [...this.data.graph_data];
    }
  }

  _getQueryParams() {
    return {
      graph_week_index: this.graphWeekIndex,
    };
  }

  stockFormatter = unitFormatter('t', 1);
  kPiecesFormatter = unitFormatter('k', 1);
  literFormatter = unitFormatter('L', 1);
  euroFormatter = unitFormatter('k€', 1);
  daysOnHandFormatter = unitFormatter('DOH', 1);
  noUnitFormatter = floatFormatter(1);

  totalStockFormatter = (value) => {
    if (this.unit === "TO") {
      return this.stockFormatter(value);
    }
    if (this.unit === "KPC") {
      return this.kPiecesFormatter(value);
    }
    if (this.unit === "L") {
      return this.literFormatter(value);
    }
    if (this.unit === "kEUR") {
      return this.euroFormatter(value);
    }
    if (this.unit === "SKUs") {
      return `${this.noUnitFormatter(value)} (SKUs)`;
    }
    return this.noUnitFormatter(value);
  };

  tooltipFormatter = (value, name) => {
    if (name.endsWith(' (Tons)')) {
      return this.stockFormatter(value);
    }
    if (name.endsWith(' (Pieces)')) {
      return this.kPiecesFormatter(value);
    }
    if (name.endsWith(' (Liters)')) {
      return this.literFormatter(value);
    }
    if (name.endsWith(' (Euros)')) {
      return this.euroFormatter(value);
    }
    return formatFloat(value, 0);
  };

  yAxisFormatter = value => {
    if (this.unit === 'TO') {
      return this.stockFormatter(value);
    }
    if (this.unit === 'KPC') {
      return this.kPiecesFormatter(value);
    }
    if (this.unit === 'L') {
      return this.literFormatter(value);
    }
    if (this.unit === 'kEUR') {
      return this.euroFormatter(value);
    }
    return formatFloat(value, 0);
  };

  onGraphWeekIndexChange = this.genericChangeValueHandler('graphWeekIndex');
  onUnitChange = this.genericChangeHandler('unit', false);

  @action onBarClick = event => {
    if (event === null || event.activeLabel === 'Dead stock') {
      return;
    }
    if (event.activeLabel === this.activeLabel) {
      this.activeLabel = null;
    } else {
      this.activeLabel = event.activeLabel;
    }
  };

  get controls() {
    if (!this.initialized) {
      return <Skeleton variant="rect" width={480} height={55} />;
    }
    return (
      <Grid container justify="flex-end" alignItems="center" spacing={4}>
        <Grid item>
          {this.weekStoreField}
        </Grid>
        <Grid item>
          <TextField variant="outlined" select label="Unit" value={this.unit} onChange={this.onUnitChange}>
            {Object.entries(this.constructor.units).map(([value, name]) =>
              <MenuItem key={value} value={value}>{name}</MenuItem>
            )}
          </TextField>
        </Grid>
        <Grid item>
          <FilterListView filterList={this.filterList} />
        </Grid>
        <Grid item>
          <DownloadButton
            href={this.weekStore.getApiUrl(
              '/inventory/?' + this.getQueryString({ export: 'xlsx', active_label: this.activeLabel })
            )}
            permission="parthenos.download_inventory"
          />
        </Grid>
      </Grid>
    );
  }

  getCells = (baseColor) => {
    const { activeLabel } = this;
    const style = { cursor: 'pointer' };
    return this.graphData.map((row, index) =>
      <Cell
        key={index}
        fill={
          activeLabel === null
            ? baseColor
            : (activeLabel === row.label ? baseColor : lighten(baseColor, 0.5))
        }
        style={style}
      />
    );
  };

  get chart() {
    if (!this.ready) {
      return <Skeleton variant="rect" height="100%" />;
    }
    const unitName = this.constructor.units[this.unit];
    return (
      <BarChart data={this.graphData} margin={{ top: 16, right: 16, bottom: 16, left: 32 }} onClick={this.onBarClick}>
        <XAxis dataKey="label" label={{ value: 'Stock / stock target (EOW)' }} height={64} />
        <YAxis tickFormatter={this.yAxisFormatter} />
        <Legend onClick={this.onLegendClick} formatter={this.legendFormatter} />
        <RechartsTooltip formatter={this.tooltipFormatter} />
        {this.getBar(
          `close_target_${this.unit}`,
          {
            stackId: 'a', name: `Within min/max target (${unitName})`, fill: athenaGreen, children: this.getCells(athenaGreen),
          },
        )}
        {this.getBar(
          `below_min_${this.unit}`,
          {
            stackId: 'a', name: `Below min (${unitName})`, fill: statRed, children: this.getCells(statRed),
          },
        )}
        {this.getBar(
          `above_max_${this.unit}`,
          {
            stackId: 'a', name: `Above max (${unitName})`, fill: athenaBlue, children: this.getCells(athenaBlue),
          },
        )}
        {this.getBar(
          `stock_no_fc_3_months_${this.unit}`,
          {
            stackId: 'a', name: `Stock without forecast in the next 3 months (${unitName})`,
            fill: lighten('#000', 0.6),
            legendType: 'none',
          },
        )}
        {this.getBar(
          `stock_no_fc_6_months_${this.unit}`,
          {
            stackId: 'a', name: `Stock without forecast in the next 6 months (${unitName})`,
            fill: lighten('#000', 0.4),
            legendType: 'none',
          },
        )}
        {this.getBar(
          `stock_no_fc_full_scope_${this.unit}`,
          {
            stackId: 'a', name: `Stock without forecast in the full scope (${unitName})`,
            fill: lighten('#000', 0.2),
            legendType: 'none',
          },
        )}
        {this.getBar(
          `blocked_stock_${this.unit}`,
          {
            stackId: 'a', name: `Blocked stock (${unitName})`,
            fill: '#000',
            legendType: 'none',
          },
        )}
      </BarChart>
    );
  }

  @computed get weeksMarks() {
    return this.projectedWeeks.map(
      (week, index) => ({ value: index, label: index === this.graphWeekIndex ? week : null })
    );
  }

  get slider() {
    if (!this.initialized) {
      return <Skeleton variant="rect" height={55} />;
    }
    return (
      <>
        <Typography>
          Projected week
        </Typography>
        <Slider
          value={this.graphWeekIndex}
          min={0}
          step={1}
          max={this.projectedWeeks.length - 1}
          valueLabelFormat={value => this.projectedWeeks[value]}
          marks={this.weeksMarks}
          onChange={this.onGraphWeekIndexChange}
        />
      </>
    );
  }

  filterTableColumn = (serializedDataFrame, columnName, columnIndex) => columnName[0] !== 'label';

  filterTableRow = (serializedDataFrame, rowIndex) => {
    const { activeLabel } = this;
    if (activeLabel === null) {
      return true;
    }
    const { data, columns } = serializedDataFrame;
    return data[rowIndex][columns.map(([column]) => column).indexOf('label')] === activeLabel;
  };

  formatTableIndexName = (serializedDataFrame, indexName, indexIndex) => this.constructor.tableIndexNames[indexName];
  formatTableColumn = (serializedDataFrame, columnsRowIndex, columnName, columnIndex) => (
    this.constructor.tableColumns[columnName]
  );

  formatTableData = (serializedDataFrame, columnIndex, rowIndex, value) => {
    const { classes } = this.props;
    let { columns, data } = serializedDataFrame;
    columns = columns.map(([column]) => column);
    const column = columns[columnIndex];
    if (column === 'label') {
      return value;
    }
    const row = data[rowIndex];
    const getValue = otherColumn => row[columns.indexOf(otherColumn)];

    if (['total_stock_TO','total_stock_L', 'total_stock_kEUR', 'total_stock_DOH'].includes(column)) {
      const unit = column.slice('total_stock_'.length);
      let totalStockClassName = classes.closeToTarget;
      if (value < getValue(`stock_min_${unit}`)) {
        totalStockClassName = classes.belowMin;
      } else if (value > getValue(`stock_max_${unit}`)) {
        totalStockClassName = classes.aboveMax;
      }
      return <span className={totalStockClassName}>{formatFloat(value, 1)}</span>;
    }
    return formatFloat(value, 1);
  };

  getTableColumnsProps = (serializedDataFrame, columnsRowIndex, columnName, columnIndex) => {
    const props = { align: 'right' };
    if (['stock_gap_TO', 'stock_gap_kEUR', 'stock_gap_L'].includes(columnName)) {
      props.rightBorder = true;
    }
    return props;
  };

  getTableDataProps = (serializedDataFrame, columnIndex, rowIndex, value) => this.getTableColumnsProps(
    serializedDataFrame, 0, serializedDataFrame.columns[columnIndex][0], columnIndex,
  );

  @computed get table() {
    if (!this.ready) {
      return <Skeleton variant="rect" height={600} />;
    }
    return (
      <DataFrame
        serializedDataFrame={this.data.table_data}
        maxHeight={600}
        filterColumn={this.filterTableColumn}
        filterRow={this.filterTableRow}
        formatIndexName={this.formatTableIndexName}
        formatColumn={this.formatTableColumn}
        formatData={this.formatTableData}
        getColumnProps={this.getTableColumnsProps}
        getDataProps={this.getTableDataProps}
      />
    );
  }

  @computed get infoMessage() {
    if (!this.ready) {
      return <Skeleton variant="rect" height={800} />;
    }
    return (
      <Typography component="div">
        <ul>
          <li> End-of-week stock is displayed. Latest stock snapshot is available as Projected Week {this.data.week_codes[-1]}</li>
          <li> Dead stock @{this.data.week_codes[-1]} is the dead stock from the latest snapshot. Dead stock @{this.data.week_codes[0]}, {this.data.week_codes[1]}... is the stock with infinite stock coverage (no demand within the next 13 weeks)</li>
        </ul>
      </Typography>
    );
  }

  @computed get totalStock() {
    if (!this.ready){
      return null
    }
    return(
      `Total stock selected: ${this.totalStockFormatter(this.data.total_stock_data[this.unit])}`
    );
  }

  render() {
    return (
      <Grid container direction="column" wrap="nowrap" spacing={4}>
        <Grid
          item
          container
          spacing={1}
          justify="space-between"
          alignItems="center"
          wrap="nowrap"
        >
          <Grid item>
            <Header title="Inventory" />
          </Grid>
          <Grid item>
            {this.controls}
          </Grid>
        </Grid>
        <Grid item>
          <Paper>
            <Typography>
              {this.getInfoTooltip(this.infoMessage)}
              {this.totalStock}
            </Typography>
            <ResponsiveContainer aspect={27 / 9}>
              {this.chart}
            </ResponsiveContainer>
          </Paper>
        </Grid>
        <Grid item>
          {this.slider}
        </Grid>
        <Grid item>
          {this.table}
        </Grid>
      </Grid>
    );
  }
}


export default InventoryView;
